import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import React, { ReactElement } from 'react'
import IntegrationCard from 'src/feature/Integrations/IntegrationCard/IntegrationCard'
import IntegrationCardFooter from 'src/feature/Integrations/IntegrationCard/IntegrationCardFooter'
import { useTranslate } from 'src/localization/Trns'

import style from './GoogleWorkspaceIntegrationCard.style'

export type GoogleWorkspaceIntegrationStatus =
  | 'NOT_CONNECTED'
  | 'ACTIVE'
  | 'ACTION_REQUIRED'
  | 'DISCONNECTED'

type GoogleWorkspaceIntegrationCardProps = {
  name: string
  description: string
  icon: React.ReactNode
  status?: GoogleWorkspaceIntegrationStatus
  canManage: boolean
  onSetup: () => void
  onManage: () => void
  loading?: boolean
  error?: boolean
  onRetry?: () => void
}

export default function GoogleWorkspaceIntegrationCard({
  name,
  description,
  icon,
  status,
  canManage,
  onSetup,
  onManage,
  loading = false,
  error = false,
  onRetry,
}: GoogleWorkspaceIntegrationCardProps): ReactElement {
  const translate = useTranslate()

  if (loading) {
    return (
      <IntegrationCard className="GoogleWorkspaceIntegrationCard" aria-busy>
        <div css={style} className="GoogleWorkspaceIntegrationCard-content">
          <span className="GoogleWorkspaceIntegrationCard-icon">{icon}</span>
          <div className="GoogleWorkspaceIntegrationCard-copy">
            <h2>{name}</h2>
            <p>
              {translate('Loading ${integrationName} connection…', {
                integrationName: name,
              })}
            </p>
          </div>
        </div>
      </IntegrationCard>
    )
  }

  if (error || !status) {
    return (
      <IntegrationCard className="GoogleWorkspaceIntegrationCard">
        <div css={style} className="GoogleWorkspaceIntegrationCard-content">
          <span className="GoogleWorkspaceIntegrationCard-icon">{icon}</span>
          <div className="GoogleWorkspaceIntegrationCard-copy">
            <h2>{name}</h2>
            <AlertPanel variant="severe-wcag">
              {translate(
                '${integrationName} connection details could not be loaded.',
                {
                  integrationName: name,
                },
              )}
            </AlertPanel>
            {onRetry && (
              <Button variant="secondary" onClick={onRetry}>
                {translate('Try again')}
              </Button>
            )}
          </div>
        </div>
      </IntegrationCard>
    )
  }

  const isActive = status === 'ACTIVE'
  const isActionRequired = status === 'ACTION_REQUIRED'
  const translatedStatusLabel = isActive
    ? translate('Connected')
    : isActionRequired
      ? translate('Action required')
      : translate('Connect')

  const content = (
    <div css={style} className="GoogleWorkspaceIntegrationCard-content">
      <div className="GoogleWorkspaceIntegrationCard-header">
        <span className="GoogleWorkspaceIntegrationCard-icon">{icon}</span>
      </div>
      <div className="GoogleWorkspaceIntegrationCard-copy">
        <h2>{name}</h2>
        <p>{description}</p>
      </div>
      <IntegrationCardFooter
        status={{
          kind: isActive
            ? 'connected'
            : isActionRequired
              ? 'incomplete'
              : 'connect',
          label: translatedStatusLabel,
        }}
        action={
          isActive && canManage
            ? { kind: 'manage', label: translate('Manage') }
            : undefined
        }
        rightContent={
          !canManage ? (
            <span className="GoogleWorkspaceIntegrationCard-readonly">
              {translate(
                'Ask an organization owner to manage this integration.',
              )}
            </span>
          ) : undefined
        }
      />
    </div>
  )

  if (!canManage) {
    return (
      <IntegrationCard className="GoogleWorkspaceIntegrationCard">
        {content}
      </IntegrationCard>
    )
  }

  return (
    <IntegrationCard
      className="GoogleWorkspaceIntegrationCard"
      ariaLabel={`${name}: ${translatedStatusLabel}`}
      onClick={isActive ? onManage : onSetup}
    >
      {content}
    </IntegrationCard>
  )
}
